import { Component, HostBinding, OnDestroy, OnInit } from "@angular/core";
import { FormBuilder, FormControl, FormGroup } from "@angular/forms";
import { MatDialog } from "@angular/material/dialog";
import { MatTableDataSource } from "@angular/material/table";
import { PageEvent } from "@angular/material/paginator";
import { Router } from "@angular/router";
import { Subject } from "rxjs";
import { debounceTime, distinctUntilChanged, takeUntil } from "rxjs/operators";
import { ToastrTranslatedService } from "../shared/toastr-translated.service";
import { TranslateService } from "@ngx-translate/core";
import {
  getFulfillmentStageLabelKey,
  getOrderItemsSummary,
} from "../card-orders/card-order.util";
import { MergePrintCardOrdersDialogComponent } from "./merge-print-card-orders-dialog/merge-print-card-orders-dialog.component";
import { PrintCardOrder } from "./print-card-orders.interface";
import { PrintCardOrdersService } from "./print-card-orders.service";

@Component({
  selector: "app-print-card-orders",
  templateUrl: "./print-card-orders.component.html",
  styleUrls: ["./print-card-orders.component.scss"],
  standalone: false,
})
export class PrintCardOrdersComponent implements OnInit, OnDestroy {
  readonly displayedColumns = [
    "createdAt",
    "domain",
    "employee",
    "items",
    "status",
    "actions",
  ];
  readonly pageSizeOptions = [10, 20, 100];
  readonly getFulfillmentStageLabelKey = getFulfillmentStageLabelKey;
  readonly dataSource = new MatTableDataSource<PrintCardOrder>([]);
  readonly filters: FormGroup<{
    search: FormControl<string>;
  }>;
  loading = true;
  downloadingPricing = false;
  merging = false;
  total = 0;
  pageIndex = 0;
  pageSize = 20;
  private readonly destroy$ = new Subject<void>();

  @HostBinding("class.is-empty")
  get isEmpty(): boolean {
    return !this.loading && this.total === 0 && !this.isFilterActive;
  }

  constructor(
    private readonly ordersService: PrintCardOrdersService,
    private readonly toastr: ToastrTranslatedService,
    private readonly router: Router,
    private readonly translate: TranslateService,
    private readonly dialog: MatDialog,
    fb: FormBuilder,
  ) {
    this.filters = fb.nonNullable.group({
      search: "",
    });
  }

  get isFilterActive(): boolean {
    return Boolean(this.filters.controls.search.value.trim());
  }

  get showNoSearchResults(): boolean {
    return !this.loading && this.isFilterActive && this.total === 0;
  }

  ngOnInit(): void {
    this.filters.controls.search.valueChanges
      .pipe(debounceTime(300), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(() => {
        this.pageIndex = 0;
        this.loadOrders();
      });

    this.loadOrders();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadOrders(): void {
    this.loading = true;
    this.ordersService
      .listOrders({
        page: this.pageIndex,
        limit: this.pageSize,
        phrase: this.filters.controls.search.value.trim() || undefined,
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.dataSource.data = response.data;
          this.total = response.total;
          this.loading = false;
        },
        error: () => {
          this.loading = false;
          this.toastr.error("printCardOrders.loadFailed");
        },
      });
  }

  openMergeDialog(): void {
    if (this.merging) {
      return;
    }
    this.merging = true;
    const dialogRef = this.dialog.open(MergePrintCardOrdersDialogComponent, {
      autoFocus: false,
      width: "820px",
      maxWidth: "96vw",
    });
    dialogRef
      .afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((order: PrintCardOrder | undefined) => {
        this.merging = false;
        if (!order?.printCardOrderId) {
          return;
        }
        this.loadOrders();
        void this.router.navigate([
          "/admin/print-card-orders",
          order.printCardOrderId,
        ]);
      });
  }

  downloadPricing(): void {
    if (this.downloadingPricing) {
      return;
    }
    this.downloadingPricing = true;
    this.ordersService
      .downloadDrukExpressPricing()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: ({ blob, filename }) => {
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = url;
          link.download = filename;
          link.click();
          window.URL.revokeObjectURL(url);
          this.downloadingPricing = false;
          this.toastr.success("printCardOrders.downloadPricingSuccess");
        },
        error: () => {
          this.downloadingPricing = false;
          this.toastr.error("printCardOrders.downloadPricingFailed");
        },
      });
  }

  changePage(event: PageEvent): void {
    this.pageIndex = event.pageIndex;
    this.pageSize = event.pageSize;
    this.loadOrders();
  }

  orderDetailLink(order: PrintCardOrder): string[] | null {
    const orderId = (order.printCardOrderId || "").trim();
    return orderId ? ["/admin/print-card-orders", orderId] : null;
  }

  openOrder(order: PrintCardOrder): void {
    const link = this.orderDetailLink(order);
    if (!link) {
      this.toastr.error("printCardOrders.loadFailed");
      return;
    }
    void this.router.navigate(link);
  }

  orderItemsSummary(order: PrintCardOrder): string {
    return getOrderItemsSummary(
      order,
      (key, params) => this.translate.instant(key, params) as string,
    );
  }
}
